/**
 * Sub-Organization · Organization · Manager · Position — the readings of the same documents.
 *
 * NOT A FILTER, AND THE DIFFERENCE MATTERS. The Filter By band below narrows a list that has
 * already been decided; these change WHICH DOCUMENTS EXIST for this caller. The list, the search
 * and every lifecycle action here forward the persona to QMS, which answers them — so widening to
 * Organization is not "show me more rows", it is "answer as somebody entitled to the whole
 * organization". That is why it sits above the filters rather than inside them.
 *
 * WHAT IS OFFERED IS NOT WHAT IS ENFORCED. `may_assume` refuses an ungranted mode whatever the
 * client sends, so hiding a mode changes nothing about what can be read — it changes whether
 * somebody is shown a tab that would answer 403. A control that fails when you press it reads as a
 * broken product rather than as a permission you were never given.
 *
 * NOTHING IS DRAWN WHEN THERE IS NOTHING TO CHOOSE. availableModes returns [] for somebody with one
 * persona and no widening grants, and this renders null rather than a ruled band with one tab in
 * it — a lone tab describes the only view its owner will ever get. t1 shipped the empty band by
 * accident and Ed reported it against Documents; it is the ordinary case for a new tenant's team
 * member, so it is worth not repeating.
 */
import React from "react";
import { Box, Typography } from "@mui/material";

import useAppContext from "../../util/AppContext";
import EventBus from "../../util/EventBus";
import { availableModes } from "../../util/VisibilityModes";
import { Blue3, Grey2, Grey6, Grey17 } from "../../theme/styles";

export default function VisibilityModeTabs() {
    const sessionContext = useAppContext()?.sessionContext as any;

    const active = sessionContext?.getActiveVisibility?.();
    const activeMode = active?.Mode ?? "";
    const modes = availableModes(activeMode,
                                 sessionContext?.getPrivileges?.(),
                                 sessionContext?.getAccount?.());

    if (modes.length === 0) return null;

    const select = (mode: string) => {
        // THE VALUE IS KEPT. A mode is a reading of the persona the user already holds -- the same
        // position, read as far as the organization around it -- so replacing the value here would
        // change who they are as well as how far they can see.
        sessionContext.setActiveVisibility(mode, active?.Value ?? "");
        // The page listens for this rather than reading storage on every render: changing persona
        // changes which records exist for this caller, so a list that did not refetch would keep
        // showing the last persona's documents under the new one's name.
        EventBus.visibility.emit("change-visibility", sessionContext.getActiveVisibility());
    };

    return (
        <Box sx={{ px: 4, borderBottom: `1px solid ${Grey2}` }}>
            <Box sx={{ maxWidth: 1280, mx: "auto", display: "flex", alignItems: "flex-end", gap: 2.75, pt: 1.5 }}>
                {modes.map(one => (
                    <Typography key={one.mode} onClick={() => select(one.mode)}
                                role="button" tabIndex={0}
                                aria-label={`Read as ${one.label}`}
                                aria-current={activeMode === one.mode ? "true" : undefined}
                                onKeyDown={e => e.key === "Enter" && select(one.mode)}
                                sx={{ cursor: "pointer", pb: "6px", fontSize: 14,
                                      fontWeight: activeMode === one.mode ? 600 : 400,
                                      color: activeMode === one.mode ? Grey17 : Grey6,
                                      borderBottom: activeMode === one.mode
                                          ? `2px solid ${Blue3}` : "2px solid transparent" }}>
                        {one.label}
                    </Typography>
                ))}
            </Box>
        </Box>
    );
}
